import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import { ComputedCaptionsStyles, Page, RemotionConfig } from '../../types';
import { SelectionOutline } from '../Outlines/SelectionOutline';
import { RemotionUsage, useRemotionUsage } from '../../MainPlayer/RemotionUsageContext/RemotionUsageContext';
import { SelectionElementType } from '../Outlines/types';
import { CompositionConfig, ElementPositions } from '../../MainPlayer/types/CompositionConfig';
import { getTransitionWordInStyles } from '@/App/remotion/libs/transitions/transitionWordIn';
import { getTransitionPageInStyles } from '@/App/remotion/libs/transitions/transitionPageIn';
import { TransitionInEffect } from '@/App/remotion/libs/transitions/types';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { throttle } from '@/libs/utils';

interface Props extends Pick<RemotionConfig, 'onOutlineRelease' | 'canEditCaptions'> {
  onOutlineResize: CompositionConfig['onOutlineResize'];
  page: Page;
  disableCaptionsDragging?: boolean;
  disableCaptionsResize?: boolean;
  computedCaptionsStyles: ComputedCaptionsStyles;
}

export default function CaptionsPageInternal({
  page,
  onOutlineRelease,
  canEditCaptions,
  disableCaptionsDragging = false,
  disableCaptionsResize = false,
  onOutlineResize,
  computedCaptionsStyles
}: Props) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const [newPositions, setNewPositions] = useState({
    top: page.style.top,
    left: page.style.left,
    width: page.style.width,
    height: page.style.height
  });

  const throttledRecalculateRef = useRef<((_positions: ElementPositions) => void) | null>(null);

  useEffect(() => {
    if (onOutlineResize) {
      throttledRecalculateRef.current = throttle((positions: ElementPositions) => {
        onOutlineResize(positions, SelectionElementType.CAPTIONS);
      }, 0);
    }
  }, [onOutlineResize]);

  useEffect(() => {
    setNewPositions({ top: page.style.top, left: page.style.left, width: page.style.width, height: page.style.height });
  }, [page.style.top, page.style.left, page.style.width, page.style.height]);

  const updatePositions = useCallback(
    positions => {
      if (disableCaptionsDragging) {
        return;
      }
      setNewPositions(p => ({ ...p, ...positions }));

      if (throttledRecalculateRef.current) {
        throttledRecalculateRef.current({ ...newPositions, ...positions });
      }
    },
    [disableCaptionsDragging, newPositions]
  );

  const onSelectionOutlineRelease = useCallback(
    positions => {
      if (disableCaptionsDragging) {
        return;
      }

      const isPositionsSame =
        positions.top === newPositions.top &&
        positions.left === newPositions.left &&
        positions.width === newPositions.width &&
        positions.height === newPositions.height;
      if (isPositionsSame) {
        return;
      }

      onOutlineRelease?.(positions);
    },
    [onOutlineRelease, newPositions, disableCaptionsDragging]
  );

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.UpdateCaptionsPosition, updatePositions);

    return () => {
      EventBus.off(CustomEvents.UpdateCaptionsPosition, eventListener);
    };
  }, [updatePositions]);

  const { usage } = useRemotionUsage();

  const pageAnimationStyles = useMemo(() => {
    if (!page.transitions?.pageIn || page.transitions?.pageIn === TransitionInEffect.None) {
      return {};
    }

    return getTransitionPageInStyles({
      frame,
      fps,
      animationType: page.transitions?.pageIn
    });
  }, [page.transitions?.pageIn, frame, fps]);

  const inheritedStyles = useMemo(() => {
    return {
      fontFamily: 'inherit',
      lineHeight: 'inherit'
    };
  }, []);

  return (
    <AbsoluteFill className="pointer-events-none select-none">
      <div
        style={{ ...page.style, ...newPositions }}
        id={usage === RemotionUsage.THUMBNAIL ? undefined : 'captions-page-internal'}
      >
        <div
          data-testid="captions-style-container"
          style={{ ...page.groupedTokensStyle, ...pageAnimationStyles, ...computedCaptionsStyles.groupedTokensStyle }}
        >
          {page.tokens.map((t, index) => {
            const startRelativeToSequence = t.fromFrame - page.fromFrame;
            const endRelativeToSequence = t.toFrame - page.fromFrame;
            const active = startRelativeToSequence <= frame && endRelativeToSequence > frame;

            const tokenStyle: React.CSSProperties = {
              ...(active ? page.activeTokenStyle : page.inactiveTokenStyle),
              ...(page.transitions?.wordIn && page.transitions?.wordIn !== TransitionInEffect.None
                ? getTransitionWordInStyles({
                    frame,
                    startFrame: startRelativeToSequence,
                    animationType: page.transitions?.wordIn,
                    fps
                  })
                : {})
            };

            return (
              <div key={t.fromFrame} style={inheritedStyles} id={'captions-page-token-' + index}>
                <span className="relative" style={inheritedStyles}>
                  {page.highlightBoxStyle && active && (
                    <div
                      data-testid="captions-page-highlight-box"
                      className="absolute z-0"
                      style={page.highlightBoxStyle}
                    />
                  )}
                  <span id={'captions-word-style-' + index} className="relative z-[1]" style={tokenStyle}>
                    {t.text}
                  </span>
                </span>
                {index !== page.tokens.length - 1 && <span style={page.inactiveTokenStyle}>{'\u00A0'}</span>}
              </div>
            );
          })}
        </div>
      </div>
      {canEditCaptions && (
        <SelectionOutline
          positions={{ ...newPositions }}
          updatePositions={updatePositions}
          onOutlineRelease={onSelectionOutlineRelease}
          allowResize={!disableCaptionsResize}
          elementType={SelectionElementType.CAPTIONS}
          data={{ type: SelectionElementType.CAPTIONS }}
          minimumWidthPercentage={10}
        />
      )}
    </AbsoluteFill>
  );
}
